import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import ExportPreview from "@/components/ExportPreview";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Eye, FileText } from "lucide-react";

export default function ReportProject() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(false);
  const [full, setFull] = useState(null);

  useEffect(() => { api.get("/payroll/periods").then((r) => setPeriods(r.data)); }, []);
  useEffect(() => { if (periodId) api.get(`/reports/project-recap?period_id=${periodId}`).then((r) => setData(r.data)); }, [periodId]);

  const rows = data?.rows || [];
  return (
    <div className="space-y-6">
      <div className="no-print space-y-4">
        <PageHeader title="Rekap Per Proyek" subtitle="Biaya tenaga kerja + belanja per proyek">
          {rows.length > 0 && <>
            <Button variant="outline" onClick={() => setPreview(true)} data-testid="proj-rekap-preview"><Eye className="h-4 w-4 mr-1" /> Pratinjau & Unduh</Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="proj-rekap-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
          </>}
        </PageHeader>
        <Card className="p-4 max-w-md">
          <F label="Pilih Periode">
            <Select value={periodId} onValueChange={setPeriodId}>
              <SelectTrigger data-testid="proj-rekap-period"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </Card>
      </div>

      {data && (
        <div className="print-area grid gap-3 sm:grid-cols-2">
          {rows.map((r, i) => (
            <Card key={i} className="p-5 printable-slip">
              <div className="flex items-center justify-between border-b pb-2 mb-3">
                <h3 className="font-heading font-bold text-slate-800">{r.project_nama}</h3>
                <span className="text-xs text-slate-500">{r.jumlah_pegawai} pegawai</span>
              </div>
              <div className="space-y-1.5 text-sm">
                {[["Total Hari Kerja", `${r.total_hari} hari`], ["Total Jam Lembur", `${r.total_lembur} jam`],
                  ["Gaji Pokok", rupiah(r.gaji_pokok)], ["Uang Makan", rupiah(r.uang_makan)],
                  ["Uang Luar Kota", rupiah(r.uang_luar_kota)], ["Uang Lembur", rupiah(r.uang_lembur)],
                  ["Tunjangan", rupiah(r.total_tunjangan)], ["Potongan", rupiah(r.total_potongan)]].map(([l, v]) => (
                  <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-mono">{v}</span></div>
                ))}
                <div className="flex justify-between border-t pt-2 mt-2 font-semibold"><span className="text-slate-600">Total Gaji</span><span className="font-mono text-slate-800">{rupiah(r.total)}</span></div>
                <div className="flex justify-between font-semibold"><span className="text-slate-600">Belanja (Material/Tools)</span><span className="font-mono text-rose-700">{rupiah(r.total_belanja)}</span></div>
                <div className="flex justify-between border-t pt-2 mt-2 font-bold"><span>GRAND TOTAL</span><span className="font-mono text-blue-800">{rupiah(r.grand_total)}</span></div>
              </div>
              {r.project_id && (
                <Button variant="outline" size="sm" className="mt-3 w-full no-print" data-testid={`proj-full-${i}`}
                  onClick={() => setFull({ project_id: r.project_id, project_nama: r.project_nama })}>
                  <FileText className="h-4 w-4 mr-1" /> Laporan Lengkap (PDF)
                </Button>
              )}
            </Card>
          ))}
          {rows.length === 0 && <div className="text-slate-400 py-8">Tidak ada data.</div>}
        </div>
      )}
      <ExportPreview open={preview} onClose={() => setPreview(false)} title="Pratinjau Rekap Per Proyek"
        pdfUrl={periodId ? `/reports/project-recap/pdf?period_id=${periodId}` : null}
        onPrint={() => window.print()}
        items={[{ label: "Unduh PDF", url: `/reports/project-recap/pdf?period_id=${periodId}`, filename: "rekap_proyek.pdf" }]} />
      <ExportPreview open={!!full} onClose={() => setFull(null)} title={`Laporan Biaya Proyek — ${full?.project_nama || ""}`}
        pdfUrl={full ? `/reports/project-full/pdf?period_id=${periodId}&project_id=${full.project_id}` : null}
        items={full ? [
          { label: "Unduh PDF Lengkap", url: `/reports/project-full/pdf?period_id=${periodId}&project_id=${full.project_id}`, filename: `laporan_biaya_${full.project_nama}.pdf` },
          { label: "Unduh Excel", url: `/reports/project-full/xlsx?period_id=${periodId}&project_id=${full.project_id}`, filename: `laporan_biaya_${full.project_nama}.xlsx` },
        ] : []} />
    </div>
  );
}
